// Copyright 2026 Bitech.id. Licensed under the MIT License.

import type { BiwaveClient, BreadcrumbLevel } from '@biwave/core';

type OriginalFn = (...args: unknown[]) => void;

const LEVEL_MAP: Record<string, BreadcrumbLevel> = {
  log: 'info',
  info: 'info',
  warn: 'warning',
  error: 'error',
  debug: 'info',
};

export function installConsoleBreadcrumbs(client: BiwaveClient): () => void {
  const methods = ['log', 'info', 'warn', 'error', 'debug'] as const;
  const originals = new Map<string, OriginalFn>();

  for (const method of methods) {
    const original = (console as unknown as Record<string, OriginalFn>)[method]?.bind(console);
    if (!original) continue;
    originals.set(method, original);

    (console as unknown as Record<string, OriginalFn>)[method] = (...args: unknown[]) => {
      // Don't breadcrumb the SDK's own internal logging (transport
      // warnings, etc.) — that's noise about the SDK, not the app.
      const isOwnLog = typeof args[0] === 'string' && args[0].startsWith('[Biwave]');
      if (!isOwnLog) {
        client.addBreadcrumb({
          type: 'console',
          category: `console.${method}`,
          level: LEVEL_MAP[method] ?? 'info',
          message: args.map((a) => stringify(a)).join(' '),
        });
      }
      original(...args);
    };
  }

  return () => {
    for (const [method, original] of originals) {
      (console as unknown as Record<string, OriginalFn>)[method] = original;
    }
  };
}

function stringify(value: unknown): string {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}
